import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Users, Loader2, CheckCircle, XCircle, AlertCircle,
  TrendingUp, Target, Award, Filter, Eye, Trash2, Mail, Phone
} from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const getDecisionBadge = (decision) => {
  switch (decision) {
    case 'A_RECRUTER':
      return <Badge className="bg-[#4C8F6E] text-white"><CheckCircle className="h-3 w-3 mr-1" />À recruter</Badge>;
    case 'A_TESTER':
      return <Badge className="bg-[#0F2744] text-white"><AlertCircle className="h-3 w-3 mr-1" />À tester</Badge>;
    case 'REJETER':
      return <Badge className="bg-[#8B2020] text-white"><XCircle className="h-3 w-3 mr-1" />Rejeter</Badge>;
    default:
      return <Badge variant="outline">{decision}</Badge>;
  }
};

const ScoreBar = ({ score, max, label }) => {
  const pct = max > 0 ? (score / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-500 w-20 text-right">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-2">
        <div
          className="h-2 rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: pct >= 70 ? '#4C8F6E' : pct >= 50 ? '#0F2744' : '#0F2744' }}
        />
      </div>
      <span className="text-xs font-medium text-[#0F2744] w-12">{score}/{max}</span>
    </div>
  );
};

const QualificationDashboardPage = () => {
  const [qualifications, setQualifications] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterProfile, setFilterProfile] = useState('all');
  const [filterDecision, setFilterDecision] = useState('all');

  useEffect(() => {
    fetchQualifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProfile, filterDecision]);

  const fetchQualifications = async () => {
    setLoading(true);
    try {
      let url = '/admin/qualifications?limit=100';
      if (filterProfile !== 'all') url += `&profile_type=${filterProfile}`;
      if (filterDecision !== 'all') url += `&decision=${filterDecision}`;
      
      const [qualRes, statsRes] = await Promise.all([
        api.get(url),
        api.get('/admin/qualifications/stats')
      ]);
      
      if (qualRes.ok) {
        const data = await qualRes.json();
        setQualifications(data.qualifications || []);
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch qualifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteQualification = async (id) => {
    if (!window.confirm('Supprimer cette qualification ?')) return;
    try {
      const res = await api.delete(`/admin/qualifications/${id}`);
      if (res.ok) {
        toast.success('Qualification supprimée');
        fetchQualifications();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8" data-testid="qualification-dashboard">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-tp-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8" data-testid="qualification-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold text-tp-text">Qualifications</h1>
          <p className="text-sm text-tp-text-light mt-1">Évaluation et scoring des candidats</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card className="p-3 sm:p-4 border-tp-border">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-[#0F2744]/10 rounded-lg">
              <Users className="h-4 w-4 text-[#0F2744]" />
            </div>
            <div>
              <p className="text-xl font-semibold text-tp-text">{stats.total || 0}</p>
              <p className="text-[10px] text-tp-text-light">Total évalués</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 sm:p-4 border-[#4C8F6E]/30 bg-[#4C8F6E]/5">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-[#4C8F6E]/10 rounded-lg">
              <Award className="h-4 w-4 text-[#4C8F6E]" />
            </div>
            <div>
              <p className="text-xl font-semibold text-[#4C8F6E]">{stats.a_recruter || 0}</p>
              <p className="text-[10px] text-[#4C8F6E]">À recruter</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 sm:p-4 border-[#0F2744]/20 bg-[#0F2744]/5">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-[#0F2744]/10 rounded-lg">
              <Target className="h-4 w-4 text-[#0F2744]" />
            </div>
            <div>
              <p className="text-xl font-semibold text-[#0F2744]">{stats.a_tester || 0}</p>
              <p className="text-[10px] text-[#0F2744]/70">À tester</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 sm:p-4 border-tp-border">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gray-100 rounded-lg">
              <TrendingUp className="h-4 w-4 text-gray-500" />
            </div>
            <div>
              <p className="text-xl font-semibold text-tp-text">{stats.avg_score || 0}</p>
              <p className="text-[10px] text-tp-text-light">Score moyen</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4 border-tp-border">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Filter className="h-5 w-5 text-tp-text-light hidden sm:block" />
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Select value={filterProfile} onValueChange={setFilterProfile}>
              <SelectTrigger className="w-full sm:w-48"><SelectValue placeholder="Profil" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les profils</SelectItem>
                <SelectItem value="assistant_virtuel">Assistant Virtuel</SelectItem>
                <SelectItem value="talent_technique">Talent Technique</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterDecision} onValueChange={setFilterDecision}>
              <SelectTrigger className="w-full sm:w-48"><SelectValue placeholder="Décision" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes décisions</SelectItem>
                <SelectItem value="A_RECRUTER">À recruter</SelectItem>
                <SelectItem value="A_TESTER">À tester</SelectItem>
                <SelectItem value="REJETER">Rejeter</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* List */}
      {qualifications.length === 0 ? (
        <Card className="p-12 text-center border-tp-border">
          <Users className="h-12 w-12 text-tp-text-light mx-auto mb-4" />
          <p className="text-tp-text-light">Aucune qualification trouvée</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {qualifications.map((q) => (
            <Card key={q.id} className="p-4 sm:p-5 border-tp-border hover:shadow-md transition-shadow" data-testid={`qualification-card-${q.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                {/* Info candidat */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-base font-semibold text-tp-text truncate">{q.candidate_name}</h3>
                    {getDecisionBadge(q.decision)}
                  </div>
                  
                  {/* Coordonnées */}
                  <div className="flex flex-wrap items-center gap-3 mb-3 text-sm text-tp-text-light">
                    {q.candidate_email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {q.candidate_email}
                      </span>
                    )}
                    {q.candidate_phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {q.candidate_phone}
                      </span>
                    )}
                  </div>
                  
                  {/* Score bars */}
                  <div className="space-y-1.5">
                    {q.profile_type === 'assistant_virtuel' ? (
                      <>
                        <ScoreBar score={q.score_cognitive} max={20} label="Cognitif" />
                        <ScoreBar score={q.score_social} max={30} label="Social" />
                        <ScoreBar score={q.score_execution} max={50} label="Exécution" />
                      </>
                    ) : (
                      <>
                        <ScoreBar score={q.score_cognitive} max={30} label="Cognitif" />
                        <ScoreBar score={q.score_mental} max={30} label="Mental" />
                        <ScoreBar score={q.score_execution} max={40} label="Exécution" />
                      </>
                    )}
                  </div>

                  {/* Summary */}
                  {q.summary && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-tp-text-light">{q.summary}</p>
                      <div className="flex gap-6 mt-2">
                        {q.strengths?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-[#4C8F6E] mb-1">Points forts</p>
                            <ul className="text-xs text-tp-text-light">
                              {q.strengths.slice(0, 3).map((s, i) => <li key={`str-${s}-${i}`}>+ {s}</li>)}
                            </ul>
                          </div>
                        )}
                        {q.weaknesses?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-[#0F2744]/60 mb-1">À améliorer</p>
                            <ul className="text-xs text-tp-text-light">
                              {q.weaknesses.slice(0, 3).map((w, i) => <li key={`weak-${w}-${i}`}>- {w}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Score + Actions */}
                <div className="flex sm:flex-col items-center justify-between sm:justify-start gap-3 pt-3 sm:pt-0 border-t sm:border-t-0 border-gray-100">
                  <div className="text-center">
                    <div className={`inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full border-3 ${
                      q.score_total >= 70 ? 'border-[#4C8F6E] bg-[#4C8F6E]/5' :
                      q.score_total >= 50 ? 'border-[#0F2744] bg-[#0F2744]/5' :
                      'border-[#0F2744]/30 bg-gray-50'
                    }`} style={{ borderWidth: '3px' }}>
                      <span className={`text-lg sm:text-xl font-bold ${
                        q.score_total >= 70 ? 'text-[#4C8F6E]' :
                        q.score_total >= 50 ? 'text-[#0F2744]' :
                        'text-[#0F2744]/50'
                      }`}>
                        {q.score_total}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">sur 100</p>
                  </div>

                  <div className="flex sm:flex-col gap-2">
                    {q.candidate_id && (
                      <Link to={`/admin/candidates/${q.candidate_id}`}>
                        <Button variant="outline" size="sm" className="border-tp-border">
                          <Eye className="h-4 w-4 sm:mr-1" />
                          <span className="hidden sm:inline">Profil</span>
                        </Button>
                      </Link>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-[#8B2020]/30 text-[#8B2020] hover:bg-[#8B2020]/5"
                      onClick={() => deleteQualification(q.id)}
                      data-testid={`delete-qual-${q.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default QualificationDashboardPage;
